import React from 'react'

import Icon from '@/components/Icon'
import Image from '@/components/Image'
import RetainQueryLinkDefault from '@/components/RetainQueryLink/RetainQueryLinkDefault'
import { useQRCodeDrawer } from '@/contexts/qrcodeDrawer'
import { MenuColumn } from '@/types/cms'
import { getUrlSearchParams } from '@/utils/searchParams'

import styles from '../../Header.module.css'
import DownloadAppMenu from '../DownloadAppMenu'
import { MachinesColumn } from '../MachinesColumn'
import { MenuDesktopWithSubMenu } from '../MenuDesktopWithSubMenu'

type Props = {
  columns: MenuColumn[]
}

export function MenuDesktopColumns({ columns }: Props) {
  const { toggleDrawer, setQRCodelink, qrCodeModelValue } = useQRCodeDrawer()

  const atendimentoColumn = columns.find(
    (column) => column.title === 'Atendimento'
  )
  const otherColumns = columns.filter(
    (column) => column.title !== 'Atendimento'
  )

  return (
    <>
      {otherColumns.map((column) =>
        column?.menu_items_groups[0]?.menu_items?.length > 0 ||
        column?.download_app ? (
          <MenuDesktopWithSubMenu
            id={column?.title}
            title={column.title}
            key={`header-link-${column.title}`}
          >
            {column?.download_app ? (
              <DownloadAppMenu />
            ) : (
              <div className={`${styles.menu} py-24`}>
                {column.menu_items_groups.map((group, index) => (
                  <React.Fragment key={index}>
                    {index !== 0 && <hr className="my-8 bg-display-200" />}
                    {group.title && (
                      <p className="px-16 pt-16 mb-8 font-bold">
                        {group.title}
                      </p>
                    )}
                    {group.menu_items.map(
                      (sub) =>
                        sub.link && (
                          <RetainQueryLinkDefault
                            key={sub.title}
                            href={{
                              pathname: sub.link.href,
                              query: getUrlSearchParams(sub?.link?.query || '')
                            }}
                            id={sub?.title}
                            className={`flex items-center ${styles.solution}`}
                            target={column.directLink?.target}
                            ctaReference={`Sub menu | ${column.title} | ${sub.title}`}
                          >
                            {sub.icon?.name && (
                              <Icon
                                name={sub.icon.name}
                                className="max-w-[22px] max-h-[22px]"
                              />
                            )}
                            <div className="ml-12">
                              <div className="flex">
                                <p className="font-medium paragraph-16">
                                  {sub.title}
                                </p>
                              </div>
                              <p className="mt-2 text-display-600 paragraph-14">
                                {sub.description}
                              </p>
                            </div>
                          </RetainQueryLinkDefault>
                        )
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </MenuDesktopWithSubMenu>
        ) : column.directLink ? (
          <RetainQueryLinkDefault
            href={column.directLink.href}
            key={`header-link-${column.title}`}
            id={column.title}
            aria-label={column.title}
            className="px-16 lg:px-8 xl:px-16 my-auto font-medium text-display-900 hover:text-ton-500 paragraph-16 lg:paragraph-14 xl:paragraph-16 "
            target={column.directLink.target}
          >
            {column.title}
          </RetainQueryLinkDefault>
        ) : column?.isMachinesColumn ? (
          <MenuDesktopWithSubMenu
            title={column.title}
            key={`header-link-${column.title}`}
          >
            <div className={styles.menu}>
              <RetainQueryLinkDefault
                href={{ pathname: '/maquininhas' }}
                className="flex items-center"
                ctaReference={`Sub menu | ${column.title} | Compare as maquininhas`}
              >
                <Image
                  src={`ton-cms/prd/todas_2_c17bffeed7_1ddbc2afa4.webp`}
                  width={40}
                  height={60}
                  alt="Todas as Maquininhas"
                  className="max-w-full h-auto"
                />
                <div className="ml-12">
                  <p className="font-medium">Compare as maquininhas</p>
                  <p className="mt-2 text-display-600 paragraph-14">
                    Descubra a melhor maquininha pro seu negócio
                  </p>
                </div>
              </RetainQueryLinkDefault>
              <MachinesColumn />
            </div>
          </MenuDesktopWithSubMenu>
        ) : (
          <></>
        )
      )}

      {atendimentoColumn && (
        <MenuDesktopWithSubMenu
          id={atendimentoColumn.title}
          title={atendimentoColumn.title}
          key={`header-link-${atendimentoColumn.title}`}
        >
          <div className={`${styles.menu} py-24 right-0`}>
            {atendimentoColumn.menu_items_groups.map((group, index) => (
              <React.Fragment key={index}>
                {index !== 0 && <hr className="my-8 bg-display-200" />}
                {group.title && (
                  <p className="px-16 pt-16 mb-8 font-bold">{group.title}</p>
                )}
                {group.menu_items.map((sub) => {
                  const isOrderTracking = sub.title === 'Rastreio do pedido'
                  const isAttendanceOnChat =
                    sub.title === 'Atendimento no chat do App'

                  return (
                    sub.link && (
                      <RetainQueryLinkDefault
                        key={sub.title}
                        href={{
                          pathname: sub.link.href,
                          query: getUrlSearchParams(sub?.link?.query || '')
                        }}
                        onClick={(e: React.SyntheticEvent) => {
                          if (isOrderTracking) {
                            e.preventDefault()
                            setQRCodelink(sub?.link?.href || '')
                            qrCodeModelValue('default')
                            toggleDrawer()
                          }
                        }}
                        id={sub?.title}
                        className={`flex items-center ${styles.solution} ${
                          isAttendanceOnChat ? 'lg:hidden' : ''
                        }`}
                      >
                        {sub.icon?.name && (
                          <Icon
                            name={sub.icon.name}
                            className="max-w-[24px] max-h-[24px]"
                          />
                        )}
                        <div className="ml-12">
                          <div className="flex">
                            <p className="font-medium paragraph-16">
                              {sub.title}
                            </p>
                          </div>
                          <p className="mt-2 text-display-600 paragraph-14">
                            {sub.description}
                          </p>
                        </div>
                      </RetainQueryLinkDefault>
                    )
                  )
                })}
              </React.Fragment>
            ))}
          </div>
        </MenuDesktopWithSubMenu>
      )}
    </>
  )
}
